import type { DropdownOption } from 'components/DropdownListWithOptions';
import PopoverDropdownList from 'components/popovers/PopoverDropdownList';
import type { LoadingStatus } from 'components/users/flexlist/FlexList';
import type { BaseSummaryDurations, RawUserSummaryGrouped } from 'redux/modules/summaries.types';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import type { Duration } from 'types/units';
import { actionCreatorFactory } from 'typescript-fsa';

const actionCreator = actionCreatorFactory('timely/usersPage/dashboard');

export const setFilter = actionCreator<Partial<Filter & { shouldReset?: boolean }>>('SET_FILTER');
export const resetState = actionCreator('RESET_STATE');
export const showUserWeekDetailsModalForUserId = actionCreator<{ userId: number; weekKey: string }>(
  'SHOW_WEEK_DETAILS_MODAL'
);
export const closeUserWeekDetailsModal = actionCreator('CLOSE_WEEK_DETAILS_MODAL');
export const fetchedGraphTotals = actionCreator<UsersSummaryTotals>('FETCHED_GRAPH_TOTALS');
export const resetGraphTotals = actionCreator('RESET_GRAPH_TOTALS');
export const fetchedUserSummaries = actionCreator<{
  result: { users: Array<RawUserSummaryGrouped> };
  params: UsersSummaryQuery;
  maybeHasMore: boolean;
  scope?: 'forecasts';
  isVirtualizedFetch?: boolean;
}>('FETCHED_USER_SUMMARIES');

export const setUserSummariesLoadingStatus = actionCreator<Record<string, LoadingStatus>>(
  'SET_USER_SUMMARIES_LOADING_STATUS'
);

export type DashboardMode = 'sleeping' | 'burning' | 'profitable';
export type TimeframeFilterMethod = 'any_week' | 'whole_period';

export interface Filter {
  mode?: DashboardMode;
  period: number;
  percent: number;
  userIds?: Array<number>;
  teamIds?: Array<number>;
  page: number;
  limit: number;
  from: string;
  to: string;
  timeframe_filter_method: TimeframeFilterMethod;
}

export interface HoursPerWeekSummary extends BaseSummaryDurations {
  since: string;
  until: string;
  capacity: Duration;
}

export interface GraphTotals {
  usersCount: number;
  capacity: Duration;
  weeks: {
    [weekKey: string]: HoursPerWeekSummary;
  };
}

export interface WeekSummary extends BaseSummaryDurations {
  week: string;
  capacity?: Duration;
}

export type WeekSummaryWithRange = WeekSummary & {
  from?: string;
  to?: string;
};

export interface UserSummaryView {
  updated_at: string;
  totals: TotalsByUser;
  byWeek: Array<WeekSummary>;
}

export interface UserViewModel {
  id: number;
  name: string;
  url: string;
  avatar: string;
  summary?: UserSummaryView;
  active: boolean;
  deleted: boolean;
  user_level?: string;
  weekly_capacity: number;
}

export interface PeriodDropdownOption extends DropdownOption {
  period: number;
}

export interface TimeframeFilterMethodOption extends DropdownOption {
  method: TimeframeFilterMethod;
}

export const periodOptionsDict = {
  4: '4 weeks',
  6: '6 weeks',
  8: '8 weeks',
  12: '12 weeks',
};

export const timeframeFilterMethodOptionsDict = {
  any_week: 'any week',
  whole_period: 'whole period',
};

export class PeriodPopoverDropdownList extends PopoverDropdownList<PeriodDropdownOption> {}

export class TimeframeFilterMethodPopoverDropdownList extends PopoverDropdownList<TimeframeFilterMethodOption> {}

export interface TotalsByUser extends BaseSummaryDurations {
  updated_at: string;
  weekly_capacity: Duration;
}

export type WeeksByUser = Record<string, WeekSummary> & {
  lastFetch?: number;
  status?: LoadingStatus;
};

export interface State {
  filter: Filter;
  xhrStatus: XhrStatus;
  graphTotals?: GraphTotals;
  users: Array<number>;
  weeksByUser: {
    [userId: string]: WeeksByUser;
  };
  totalsByUser: {
    [userId: number]: TotalsByUser;
  };
  hasMore?: boolean;
  userWeekDetailsModal?: {
    userId: number;
    weekKey: string;
  };
  hasLoadedUsersForDropdown: boolean;
}

export interface UsersSummaryQuery {
  since?: string;
  until?: string;
  page?: number | null;
  perPage?: number | null;
  userIds?: Array<number>;
  teamIds?: Array<number>;
  totals?: boolean;
  groupBy?: 'week' | 'day' | null;
  percent?: number | null;
  filter?: DashboardMode;
  timeframe_filter_method?: TimeframeFilterMethod;
  hasAccessToTasks: boolean;
}

export interface UsersSummaryTotals extends BaseSummaryDurations {
  users: number;
  capacity: Duration;
  grouped: Array<HoursPerWeekSummary>;
}
